import { action, autorun, computed, observable } from 'mobx';
import * as moment from 'moment';
import 'moment-timezone';
import Sockette from 'sockette';
import { get } from './HTTPLib';
import { Cage, ChartData, Logger, Location } from './Models';


class Store {
	@observable name: string = ""
	@observable cages: Array<Cage> = []
	@observable locations: Array<Location> = []
	@observable loggers: Array<Logger> = []
	@observable whichTab: number = 0;
	@observable chartData: ChartData = new ChartData()
	pollIntID: any = null
	@observable viewId: string = ''
	@observable oxygenMinimum: number = 60


	// In ISO 8601 Duration format
	@computed get dailyDuration(): string {
		switch (this.whichTab) {
			case 0:
				return 'PT30M'
			case 1:
				return 'PT3H'
			case 2:
				return 'PT6H'
			case 3:
				return 'PT12H'
			case 4:
				return 'PT24H'
			case 5:
				return 'P1W'
			case 6:
				return 'P31D'
			default:
				return ''
		}
	}

	@action
	updateTab(newTabIndex: number) {
		this.whichTab = newTabIndex
		store.startPoll();
	}

	@action
	updatedOxygenMinimum(newLimit: number) {
		this.oxygenMinimum = newLimit
		setTimeout(() => { localStorage.setItem(`store_oxygenMinimum_${this.viewId}`, '' + newLimit) }, 100);
	}


	// TODO - this function does too much
	@action
	startView(viewId: string) {

		this.viewId = viewId


		// Grab the min from storage for better usability
		const oxygenMinimum: string = localStorage.getItem(`store_oxygenMinimum_${viewId}`) || '';
		if (oxygenMinimum) {
			this.oxygenMinimum = parseInt(oxygenMinimum, 10)
		}

		// Base data
		get(`/api/views/${viewId}`)
			.then(data => {
				const cages = data.cages as Array<any>
				cages.sort(cageSort)

				this.locations = data.locations

				// Don't wanna include location in the label if there is only one location on the page
				const hasManyLocations: boolean = [...new Set(cages.map(cage => cage.location))].length > 1

				this.cages = cages.map(cage => {
					cage.loggers.sort(loggerSort)
					// Don't wanna include logger name in the label if there is only one location on the page
					const hasManyLoggers = cage.loggers.length > 1;
					const loggers: Array<Logger> = cage.loggers.map((l: any) => {
						const chartLabel = `${hasManyLocations ? cage.location + ' / ' : ''}${cage.name}${hasManyLoggers ? ' / ' + l.name : ''}`
						return new Logger(l.id, l.name, chartLabel, l.colour, l.timezone, l.payload.oxygen, l.payload.temperature, l.payload.online,
							l.payload.timestamp, l.payload.oxygenCalibrated, l.payload.originAppHostId, viewId, l.payload.depth, l.monitorDepth, l.payload.depthCalibrated,
							l.oxygenOriginalReading, l.depthOriginalReading, l.monitorTemperature, l.monitorOxygen, l.monitorPH, l.payload.pH);
					})
					this.loggers = this.loggers.concat(loggers)
					return new Cage(cage.id, cage.name, cage.location, loggers)
				})
				this.name = data.name;
			})
			.catch(err => console.error(err));



		// Live updates
		let wsURL = `ws${window.location.protocol.replace('http', '')}//${window.location.host}/api/ws/${viewId}`
		if (window.location.host.indexOf('localhost') === 0) {
			wsURL = `ws://localhost:8090/api/ws/${viewId}`
		}
		new Sockette(wsURL, {
			onclose: (e: CloseEvent) => console.log('Closed!', e),
			onerror: (e: Event) => console.log('Error:', e),
			onmaximum: (e: CloseEvent) => console.log('Stop Attempting!', e),
			onmessage: (e: MessageEvent) => {
				const message: any = JSON.parse(e.data);

				if (message.type === 'LR') {
					this.loggers.forEach(logger => {
						if (logger.id === message.data.loggerId) {

							logger.oxygen = message.data.payload.oxygen
							logger.oxygenCalibrated = message.data.payload.oxygenCalibrated
							logger.depth = message.data.payload.depth
							logger.depthCalibrated = message.data.payload.depthCalibrated
							logger.temperature = message.data.payload.temperature
							logger.online = message.data.payload.online
							logger.timestamp = moment(message.data.payload.timestamp)
							logger.lastUpdated = logger.timestamp.clone().subtract(10, 'seconds').fromNow() // Wanna avoid it saying 'in a few seconds'
							logger.originAppHostId = message.data.payload.originAppHostId
							logger.oxygenOriginalReading = message.data.payload.oxygenOriginalReading;
							logger.depthOriginalReading = message.data.payload.depthOriginalReading;
							logger.pHReading = message.data.payload.pH;

						}
					})
				}
			},
			onopen: (e: Event) => console.log('Connected!', e),
			onreconnect: (e: Event) => console.log('Reconnecting...', e),
			timeout: 3000,
		});


	}

	getDailyReadings(viewId: string, duration: string) {
		if (duration === '') {
			return
		}

		get(`/api/chart/${viewId}?duration=${duration}`)
			.then((chartData: ChartData) => {
				chartData.oxygen.forEach(reading => {
					reading.x = moment(reading.x)
				})
				chartData.depth.forEach(reading => {
					reading.x = moment(reading.x)
					reading.y = reading.y * -1
				})
				chartData.ph.forEach(reading => {
					reading.x = moment(reading.x)
				})
				this.chartData = chartData
			})
			.catch(err => console.log(err));
	}

	@action
	getCSV(viewId: string, locationId: number, timeFrame: string) {
		return get(`/api/export/?viewId=${viewId}&locationId=${locationId}&timeFrame=${timeFrame}`);
	}


	@action
	startPoll() {
		if (this.pollIntID !== null) {
			clearInterval(this.pollIntID)
		}
		let interval = 60000;
		if (store.whichTab === 0) {
			interval = 5000;
		}
		this.getDailyReadings(this.viewId, this.dailyDuration)
		this.pollIntID = setInterval(() => this.getDailyReadings(this.viewId, this.dailyDuration), interval)
	}

}


function cageSort(c1: Cage, c2: Cage) {
	const locationCompare = c1.location.localeCompare(c2.location);
	if (locationCompare === 0) {
		return c1.name.localeCompare(c2.name);
	}
	return c1.location.localeCompare(c2.location);
}
function loggerSort(l1: Logger, l2: Logger) {
	return l1.name.localeCompare(l2.name);
}


const store = (window as any).store = new Store()
export default store

// Should autorun be here?
autorun(() => {
	store.startPoll();
})


// WEBPACK FOOTER //
// ./src/Store.tsx